import {
  Box,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import AuthenticationComponent from './components/Authentication';
import Canvas from './components/Canvas';
import Navigation from './components/Navigation';
import Packages from './components/Packages';
import Projects from './components/Projects';
import Secrets from './components/Secrets';
import Demo from './components/Demo';
import Templates from './components/Templates';
import { authUrl } from './config/authUrls';
import { isDesignMode } from './config/designMode';
import { DESIGN_PROJECT_ID } from './config/designFixtures';
import { useAuth } from './contexts/Auth';
import VisualOptionsPicker from './design/VisualOptionsPicker';
// import useApi from './hooks/useAPI';

const App = () => {
  const { token, login, logout, getValidToken } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // const api = useApi();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const validToken = await getValidToken();
        setIsLoading(false);
        if (!validToken) {
          // If no valid token, user needs to log in
          setAuthError(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthError('Failed to initialize authentication. Please try again.');
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [getValidToken]);

  const handleLogin = useCallback(
    async (decodedToken, authToken) => {
      setIsLoading(true);
      try {
        const response = await fetch(authUrl('/login/google'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: authToken }),
        });

        if (!response.ok) {
          const detail = await response.text().catch(() => '');
          throw new Error(`HTTP ${response.status}${detail ? `: ${detail}` : ''}`);
        }

        const data = await response.json();
        login(data.user, data.token);
        setAuthError(null);
      } catch (error) {
        console.error('Login error:', error);
        setAuthError(error.message || 'Login failed. Please try again.');
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [login]
  );

  const handleDevLogin = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(authUrl('/login/dev'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(
          `Dev login failed (${response.status}). Is auth DEV_AUTH=1? ${detail}`
        );
      }
      const data = await response.json();
      login(data.user, data.token);
      setAuthError(null);
    } catch (error) {
      console.error('Dev login error:', error);
      setAuthError(error.message || 'Dev login failed.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [login]);

  const handleLoginError = useCallback((error) => {
    console.error('Login Failed:', error);
    setAuthError('Login failed. Please try again.');
  }, []);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!token) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          background: 'var(--canvas-shell-bg, var(--canvas-bg))',
          backgroundAttachment: 'fixed',
        }}
      >
        <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '2.6rem', sm: '3.4rem' },
              letterSpacing: '0.02em',
              animation: 'canvas-picker-in 420ms ease both',
            }}
          >
            CLOUD CANVAS
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', mb: 1, maxWidth: 360, mx: 'auto' }}
          >
            Infra packages on a living canvas.
          </Typography>
          {authError && (
            <Typography variant="body2" color="error" gutterBottom>
              {authError}
            </Typography>
          )}
          <Box mt={4}>
            <AuthenticationComponent
              onLogin={handleLogin}
              onError={handleLoginError}
              onDevLogin={
                process.env.REACT_APP_DEV_AUTH === '1'
                  ? handleDevLogin
                  : undefined
              }
            />
          </Box>
        </Container>
        <VisualOptionsPicker />
      </Box>
    );
  }

  return (
    <Router>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Navigation onLogout={logout} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            overflow: 'hidden',
            background: 'var(--canvas-shell-bg, var(--canvas-bg))',
            backgroundAttachment: 'fixed',
          }}
        >
          <Routes>
            {isDesignMode() ? (
              <>
                <Route path="/canvas/:projectId" element={<Canvas />} />
                <Route path="/agents" element={<Packages />} />
                <Route path="/templates" element={<Templates />} />
                <Route
                  path="/demo"
                  element={
                    <Navigate to="/demo/tpl-harness-lifecycle" replace />
                  }
                />
                <Route path="/demo/:projectId" element={<Demo />} />
                <Route
                  path="/"
                  element={
                    <Navigate to={`/canvas/${DESIGN_PROJECT_ID}`} replace />
                  }
                />
                <Route
                  path="*"
                  element={
                    <Navigate to={`/canvas/${DESIGN_PROJECT_ID}`} replace />
                  }
                />
              </>
            ) : (
              <>
                <Route path="/" element={<Projects />} />
                <Route path="/secrets" element={<Secrets />} />
                <Route path="/agents" element={<Packages />} />
                <Route path="/templates" element={<Templates />} />
                <Route
                  path="/demo"
                  element={
                    <Navigate to="/demo/tpl-harness-lifecycle" replace />
                  }
                />
                <Route path="/demo/:projectId" element={<Demo />} />
                <Route path="/canvas/:projectId" element={<Canvas />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </Box>
        <VisualOptionsPicker />
      </Box>
    </Router>
  );
};

export default App;
